/**
 * Builds a layout for a RAD (Role Activity Diagram) by processing the hierarchical structure,
 * computing layout positions and sizes, and flattening the result back to the original format.
 *
 * This function performs a three-step process:
 * 1. Converts the RAD into a tree structure for layout calculations
 * 2. Computes layout properties (sizes, positions) for each node
 * 3. Flattens the tree back to the original RAD format with layout information
 *
 * */
import { createNode } from "./radTreeUtils.js";
import { computeLayout } from "./computeExpanses.js";
import { getSubthreads, getThreadSequence } from "@cadenza/rad-domain";

/**
 * Recursively adds subthreads to a refinement activity.
 */
const addSubthreadsToRefinement = (rad, activityNode) => {
  const { node } = activityNode;

  if (!node.class === "refinement") {
    return activityNode;
  }

  const subthreadsData = getSubthreads(rad)(node.id);
  const updatedChildren = subthreadsData.map((subthread) => {
    const subthreadNode = createNode(subthread);
    const activities = getThreadSequence(rad)(
      subthread.id,
      subthread.prestate_id
    ).map((activity) => {
      const newNode = createNode({
        ...activity,
        parent_id: activity.thread_id,
      });
      return addSubthreadsToRefinement(rad, newNode);
    });

    return {
      node: {
        ...subthreadNode.node,
        class: subthread.class,
        parent_id: node.id,
      },
      children: activities,
    };
  });

  return {
    node: activityNode.node,
    children: updatedChildren,
  };
};

/**
 * Create the vanilla tree layout for the RAD.
 */
const treeify = (rad) => {
  const { info, roles, threads } = rad;
  const root = createNode({ id: "root", name: info.name, class: "root" });

  (roles || []).forEach((role) => {
    const roleNode = createNode({
      ...role,
    });
    const roleThreads = (threads || []).filter(
      (thread) => thread.role_id === role.id
    );
    roleThreads.forEach((thread) => {
      const threadNode = createNode({
        ...thread,
      });
      const orderedActivities = getThreadSequence(rad)(
        thread.id,
        thread.prestate_id
      );

      const processedActivities = orderedActivities.map((entity) => {
        const entityNode = createNode({
          ...entity,
          id: entity.id,
          parent_id: entity.thread_id,
        });
        return addSubthreadsToRefinement(rad, entityNode);
      });

      roleNode.children.push({
        node: {
          ...threadNode.node,
          parent_id: role.id,
        },
        children: processedActivities,
      });
    });

    root.children.push(roleNode);
  });
  return root;
};

const untreeifyLayout = (layout) => {
  const roles = layout.children.map((role) => {
    return {
      ...role.node,
    };
  });

  const threads = layout.children.flatMap((role) => {
    return role.children
      .filter((thread) => thread.node.class === "thread")
      .flatMap((thread) => {
        return {
          ...thread.node,
        };
      });
  });

  const flattenActivitiesAndSubthreads = (c) => {
    return c.flatMap((entity) => {
      const flattened = [
        {
          ...entity.node,
        },
      ];
      if (entity.children && entity.children.length > 0) {
        flattened.push(...flattenActivitiesAndSubthreads(entity.children));
      }
      return flattened;
    });
  };

  const entities = layout.children.flatMap((role) => {
    return role.children.flatMap((thread) => {
      return flattenActivitiesAndSubthreads(thread.children);
    });
  });

  const activities = entities.filter((activity) =>
    ["activity", "refinement"].includes(activity.class)
  );

  const subthreads = entities.filter((activity) =>
    ["part", "case", "repeat"].includes(activity.class)
  );

  return {
    roles,
    threads: [...threads, ...subthreads],
    activities,
  };
};

const buildRADLayout = (rad) => {
  const { connections } = rad;

  // Treeify the RAD. This is okay for calculating size and positions.
  const initialLayout = treeify(rad);
  // Compute sizes recursively
  const layout = computeLayout(initialLayout, connections);
  // Untreeify the layout back to the original format
  const flatRAD = untreeifyLayout(layout);
  return {
    ...rad,
    ...flatRAD,
  };
};

export { buildRADLayout };
